import React from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { MapPin, Users, Star, Award, Heart, Globe, Camera, Navigation } from "lucide-react";

const About = () => {
  const navigate = useNavigate();

  // Structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Aaoghumen",
    "alternateName": ["Aao Ghumen", "Aao Ghume", "Aaoghume"],
    "url": window.location.origin,
    "logo": `${window.location.origin}/logo512.png`,
    "description": "Aaoghumen - Your ultimate travel companion for discovering amazing destinations, local experiences, and hidden gems. Plan your perfect trip with Aao Ghumen.",
    "sameAs": [
      "https://facebook.com/aaoghumen",
      "https://instagram.com/aaoghumen", 
      "https://twitter.com/aaoghumen"
    ],
    "foundingDate": "2024",
    "founders": {
      "@type": "Person",
      "name": "Aaoghumen Team"
    },
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "IN"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "Customer Service",
      "availableLanguage": ["Hindi", "English"]
    }
  };

  return (
    <>
      <Helmet>
        <title>About Aaoghumen - Your Ultimate Travel Companion | Aao Ghumen Guide</title>
        <meta name="description" content="Discover Aaoghumen (Aao Ghumen) - India's premier travel platform helping millions explore amazing destinations, local experiences & hidden gems. Plan your perfect trip with Aao Ghume today!" />
        <meta name="keywords" content="aaoghumen, aao ghumen, aao ghume, aaoghume, travel guide india, travel app, destination discovery, local experiences, travel planning, indian travel, tourism guide" />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content="About Aaoghumen - Your Ultimate Travel Companion | Aao Ghumen" />
        <meta property="og:description" content="Discover how Aaoghumen (Aao Ghumen) is revolutionizing travel in India with personalized trip planning, local insights, and amazing destination discovery." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${window.location.origin}/about`} />
        <meta property="og:image" content={`${window.location.origin}/logo512.png`} />
        <meta property="og:site_name" content="Aaoghumen" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About Aaoghumen - Your Ultimate Travel Companion" />
        <meta name="twitter:description" content="Discover how Aaoghumen is revolutionizing travel planning and destination discovery." />
        <meta name="twitter:image" content={`${window.location.origin}/logo512.png`} />
        
        {/* Additional SEO Tags */}
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Aaoghumen Team" />
        <link rel="canonical" href={`${window.location.origin}/about`} />
        
        {/* Local Business Schema */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-br from-orange-600 via-orange-500 to-red-500 text-white overflow-hidden">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="absolute inset-0 opacity-20">
            <svg className="w-full h-full" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="about-grid" width="20" height="20" patternUnits="userSpaceOnUse">
                  <circle cx="10" cy="10" r="1" fill="currentColor"/>
                </pattern>
              </defs>
              <rect width="100" height="100" fill="url(#about-grid)" />
            </svg>
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 py-20 sm:py-28">
            <div className="text-center">
              <div className="mb-6">
                <span className="inline-block bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-semibold">
                  🌟 About Aaoghumen
                </span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold mb-6 tracking-tight leading-tight">
                <span className="text-yellow-300">Aao Ghumen</span>
                <span className="block text-white">With Us</span>
              </h1>
              
              <p className="text-xl sm:text-2xl opacity-90 max-w-4xl mx-auto leading-relaxed mb-8">
                Discover India's hidden gems, plan perfect trips, and create unforgettable memories with Aaoghumen - your trusted travel companion for authentic local experiences.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate("/")}
                  className="bg-white text-orange-600 px-8 py-4 rounded-2xl font-semibold hover:bg-gray-100 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 text-lg"
                >
                  🗺️ Start Your Journey
                </button>
                <button
                  onClick={() => navigate("/features")}
                  className="border-2 border-white text-white px-8 py-4 rounded-2xl font-semibold hover:bg-white hover:text-orange-600 transition-all duration-300 text-lg"
                >
                  ✨ Explore Features
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 py-16">
          
          {/* Story Section */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-4">
                The Aaoghumen Story
              </h2>
              <p className="text-gray-600 text-lg max-w-3xl mx-auto">
                Born from a passion for authentic travel experiences and local discoveries
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-gray-800">
                  From "Aao Ghume" to Unforgettable Journeys
                </h3>
                <p className="text-gray-600 leading-relaxed text-lg">
                  <strong className="text-orange-600">Aaoghumen</strong> began with a simple idea - making travel planning effortless and experiences authentic. The name "Aao Ghumen" means "Come, let's wander" in Hindi, reflecting our core mission of encouraging exploration and discovery.
                </p>
                <p className="text-gray-600 leading-relaxed text-lg">
                  We understand that every traveler seeks unique experiences, local insights, and memories that last a lifetime. That's why <strong className="text-orange-600">Aao Ghume</strong> platform combines cutting-edge technology with local expertise to create personalized travel experiences.
                </p>
                <p className="text-gray-600 leading-relaxed text-lg">
                  Whether you're planning a weekend getaway or a month-long adventure, <strong className="text-orange-600">Aaoghume</strong> is your trusted companion for discovering India's incredible diversity, from bustling cities to serene landscapes.
                </p>
              </div>
              
              <div className="relative">
                <div className="bg-gradient-to-br from-orange-500 to-red-500 rounded-3xl p-8 text-white">
                  <div className="grid grid-cols-2 gap-6 text-center">
                    <div>
                      <div className="text-3xl font-bold mb-2">1M+</div>
                      <div className="text-orange-100">Happy Travelers</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold mb-2">500+</div>
                      <div className="text-orange-100">Destinations</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold mb-2">10k+</div>
                      <div className="text-orange-100">Local Experiences</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold mb-2">4.8★</div>
                      <div className="text-orange-100">User Rating</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Values Section */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-4">
                Why Choose Aaoghumen?
              </h2>
              <p className="text-gray-600 text-lg max-w-3xl mx-auto">
                Our core values drive everything we do at Aao Ghumen
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  icon: <Heart className="w-8 h-8" />,
                  title: "Authentic Experiences",
                  description: "Discover genuine local culture, traditions, and hidden gems that only locals know about."
                },
                {
                  icon: <Users className="w-8 h-8" />,
                  title: "Community Driven",
                  description: "Built by travelers, for travelers. Our community shares real experiences and honest reviews."
                },
                {
                  icon: <Navigation className="w-8 h-8" />,
                  title: "Personalized Journeys",
                  description: "AI-powered recommendations tailored to your preferences, budget, and travel style."
                },
                {
                  icon: <Award className="w-8 h-8" />,
                  title: "Trusted Platform",
                  description: "Award-winning travel platform trusted by millions of travelers across India."
                }
              ].map((value, index) => (
                <div key={index} className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 group hover:-translate-y-2">
                  <div className="text-orange-500 mb-4 group-hover:text-orange-600 transition-colors">
                    {value.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Features Highlight */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-4">
                What Makes Aaoghumen Special?
              </h2>
              <p className="text-gray-600 text-lg max-w-3xl mx-auto">
                Innovative features that make your travel planning seamless and enjoyable
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[
                {
                  icon: <MapPin className="w-6 h-6" />,
                  title: "Smart Trip Planning",
                  description: "Our AI analyzes your preferences to create perfect itineraries with optimal routes, timing, and local recommendations."
                },
                {
                  icon: <Globe className="w-6 h-6" />,
                  title: "Local Language Support",
                  description: "Travel confidently with our multi-language support including Hindi, English, and regional languages."
                },
                {
                  icon: <Camera className="w-6 h-6" />,
                  title: "Visual Discovery",
                  description: "Explore destinations through stunning photos, virtual tours, and user-generated content from real travelers."
                },
                {
                  icon: <Star className="w-6 h-6" />,
                  title: "Verified Reviews",
                  description: "Make informed decisions with authentic reviews and ratings from verified travelers who've been there."
                }
              ].map((feature, index) => (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                  <div className="flex items-start space-x-4">
                    <div className="bg-orange-100 text-orange-600 p-3 rounded-xl">
                      {feature.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800 mb-2">{feature.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mission Statement */}
          <div className="bg-gradient-to-br from-orange-600 via-orange-500 to-red-500 rounded-3xl p-12 text-white text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Our Mission
            </h2>
            <p className="text-xl sm:text-2xl opacity-90 max-w-4xl mx-auto leading-relaxed mb-8">
              To make travel accessible, authentic, and unforgettable for everyone. We believe that every journey should be a story worth sharing, and every destination should be experienced like a local.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate("/blog")}
                className="bg-white text-orange-600 px-8 py-4 rounded-2xl font-semibold hover:bg-gray-100 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 text-lg"
              >
                📖 Read Travel Stories
              </button>
              <button
                onClick={() => navigate("/gallery")}
                className="border-2 border-white text-white px-8 py-4 rounded-2xl font-semibold hover:bg-white hover:text-orange-600 transition-all duration-300 text-lg"
              >
                🖼️ View Gallery
              </button>
            </div>
          </div>
        </div>

        {/* SEO Content Footer */}
        <div className="bg-gray-50 py-16">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-800 mb-4">
                Start Your Journey with Aaoghumen Today
              </h3>
              <p className="text-gray-600 text-lg max-w-3xl mx-auto mb-8">
                Join millions of travelers who trust <strong>Aao Ghumen</strong> for discovering incredible destinations, planning perfect trips, and creating lifelong memories. Whether you search for "aaoghumen", "aao ghume", or "aaoghume" - we're here to make your travel dreams come true.
              </p>
              <button
                onClick={() => navigate("/onboarding")}
                className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-4 rounded-2xl font-semibold hover:from-orange-600 hover:to-red-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 text-lg"
              >
                🚀 Get Started Now
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default About;